import React, { PropsWithChildren, useRef, useState } from 'react';
import styled from 'styled-components';
import { Copy16 } from '@honeybook/icons';
import HBClickable from '../HBClickable/HBClickable';
import HBTooltip, { ITooltipProps } from '../HBTooltip/HBTooltip';
import HBIconButton from '../HBIconButton/HBIconButton';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';

// `detail === 0` for keyboard-synthesized / programmatic clicks; >= 1 for real pointer clicks.
const isPointerClick = (e: React.MouseEvent) => e.detail !== 0;

// Matches honeybook-react's `useCopyToClipboard` window.
const COPY_FEEDBACK_DURATION_MS = 3000;

export interface HBCopyableProps {
    /** When true, renders a copy icon button next to children instead of wrapping children in a tooltip. */
    withIcon?: boolean;
    /** Unique test identifier for the root element. */
    testId: string;
    /** Additional props forwarded to the internal HBTooltip instance. */
    tooltipProps?: Partial<ITooltipProps>;
    /** Tooltip text shown before copying. */
    tooltipTextBefore?: string;
    /** Tooltip text shown after a successful copy. */
    tooltipTextAfter?: string;
    /** When true with `withIcon`, the copy icon is always visible instead of only appearing on hover/focus. */
    alwaysShowIcon?: boolean;
    /** CSS class applied to the root element. */
    className?: string;
    /** Callback fired after text is copied to the clipboard. */
    onCopy?: () => void;
}

const HBCopyable = ({
    testId,
    children,
    withIcon,
    tooltipProps,
    tooltipTextBefore = 'Copy',
    tooltipTextAfter = 'Copied!',
    alwaysShowIcon = false,
    className,
    onCopy
}: PropsWithChildren<HBCopyableProps>) => {
    const [hasCopied, setHasCopied] = useState(false);
    // Suppresses tooltip re-open between the auto-dismiss and the next clean
    // leave from the trigger. Reset on mouseleave only — resetting on blur
    // would clear the gate during the auto-dismiss focus shift and let the
    // lingering hover re-flash the tooltip.
    const [forceTooltipClose, setForceTooltipClose] = useState(false);
    const tooltipText = hasCopied ? tooltipTextAfter : tooltipTextBefore;
    // If the cursor already left during the success window, the timer skips
    // suppression — there's no future mouseleave to lift the gate.
    const isTriggerHoveredRef = useRef(false);

    const scheduleDismiss = useDebouncedCallback((container: HTMLElement) => {
        if (isTriggerHoveredRef.current) {
            setForceTooltipClose(true);
        }
        const focused = document.activeElement;
        if (focused instanceof HTMLElement && container.contains(focused)) {
            focused.blur();
        }
    }, COPY_FEEDBACK_DURATION_MS);

    const copy = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        navigator.clipboard.writeText(ref.current?.innerText);
        setHasCopied(true);
        setForceTooltipClose(false);
        onCopy?.();

        // Keyboard / AT activations keep persistent feedback per WAI-ARIA APG.
        if (!isPointerClick(e)) {
            return;
        }

        scheduleDismiss(e.currentTarget);
    };

    const onTriggerEnter = () => {
        isTriggerHoveredRef.current = true;
    };

    const onTriggerLeave = () => {
        isTriggerHoveredRef.current = false;
        setForceTooltipClose(false);
    };

    const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        // `relatedTarget` check skips the reset when focus moves between
        // Container and its descendants (e.g. icon button).
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setHasCopied(false);
        }
    };

    const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
        // handling for if button is wrapped with a tag, it should not initiate it (onClick's stop propagation is not enough)
        if (ref.current?.closest('a')) {
            e.preventDefault();
        }
    };

    const ref = useRef<HTMLDivElement>(null);

    const commonProps = {
        className,
        testId,
        ref,
        onClickCapture,
        onClick: copy,
        onBlur
    };

    if (withIcon) {
        // Trigger-hover handlers go on the icon-scoped wrapper, not Container
        // — leaving just the icon (while staying inside Container) is enough
        // to lift suppression and rearm the next hover.
        return (
            <Container {...commonProps}>
                {children}
                <TooltipScope
                    onMouseEnter={onTriggerEnter}
                    onMouseLeave={onTriggerLeave}
                >
                    <HBTooltip
                        target={
                            // @ts-ignore
                            <StyledIconButton
                                $alwaysVisible={alwaysShowIcon}
                                icon={Copy16}
                                aria-label="copy"
                                size="xs"
                                testIdName="copy"
                                buttonTheme="subtle-invisible"
                            />
                        }
                        placement="top"
                        {...tooltipProps}
                        forceClose={forceTooltipClose}
                    >
                        {tooltipText}
                    </HBTooltip>
                </TooltipScope>
            </Container>
        );
    }

    return (
        <Container
            {...commonProps}
            onMouseEnter={onTriggerEnter}
            onMouseLeave={onTriggerLeave}
        >
            <HBTooltip
                target={children}
                placement="top"
                {...tooltipProps}
                forceClose={forceTooltipClose}
            >
                {tooltipText}
            </HBTooltip>
        </Container>
    );
};

export default HBCopyable;

const StyledIconButton = styled(HBIconButton)<{ $alwaysVisible?: boolean }>`
    opacity: ${({ $alwaysVisible }) => ($alwaysVisible ? 1 : 0)};
`;

const Container = styled(HBClickable)`
    display: flex;
    align-items: center;
    :hover ${StyledIconButton}, :focus-within ${StyledIconButton} {
        opacity: 1;
    }
`;

// `margin-left` on the wrapper (not `padding`, not on HBTooltip's inner
// `targetContainerStyle`) keeps the 8 px gap outside the wrapper's bounds —
// otherwise crossing the gap doesn't fire `mouseleave` and suppression sticks.
const TooltipScope = styled.span`
    display: inline-flex;
    margin-left: 8px;
`;
